import { Box, Button } from "@mui/material";
import Stack from "@mui/material/Stack";
import { useEffect, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { getDossier } from "../../../api/dossier/getDossier";
import { getVehicleAspects } from "../../../api/vehicle/getVehicleAspects";
import { searchVehicleVariants } from "../../../api/vehicle/searchVehicleVariants";
import APModal from "../../../components/APModal";
import APSelectDropDown from "../../../components/APSelectDropDown";
import APButton from "../../../components/buttons/APButton";
import { APButtonLink } from "../../../components/buttons/APButtonLink";
import APContactHelpButton from "../../../components/buttons/APContactHelpButton";
import BackButton from "../../../components/buttons/BackButton";
import { ExpandCollapseAllButton } from "../../../components/buttons/ExpandCollapseAllButton";
import HomeButton from "../../../components/buttons/HomeButton";
import InfoButton from "../../../components/buttons/InfoButton";
import OverwriteQuoteModal from "../../../components/modals/OverwriteQuoteModal";
import PageContainer from "../../../components/PageContainer";
import PaginationContainer from "../../../components/PaginationContainer";
import RestrictedAppScope from "../../../components/RestrictedAppScope";
import CopyExternalUrlField from "../../../components/utils/CopyExternalUrlField";
import CopyMarketingUrl from "../../../components/utils/CopyMarketingUrl";
import { buildQuoteQueryString } from "../../../helpers/quoteParams";
import {
  buildVehicleAspectOptions,
  buildVehicleVariantSearchInputsFromParams,
  buildVehicleVariantSearchQueryFromParams,
  VehicleVariantFieldLabelMap,
  VehicleVariantSearchSortOption,
  VehicleVariantSearchSortOptionsList,
  VehicleVariantValueFormatters,
} from "../../../helpers/vehicle";
import { useAIConsent } from "../../../hooks/useAIConsent";
import { useExpandCollapseAll } from "../../../hooks/useExpandCollapseAll";
import { useFaqs } from "../../../hooks/useFaqs";
import { useQuoteLimit } from "../../../hooks/useQuoteLimit";
import { useAuth } from "../../../providers/AuthProvider";
import {
  APVehicleVariant,
  VehicleVariantSearchFilters,
} from "../../../types/Vehicles";
import APStickyHeader from "../components/APStickyHeader";
import VehicleSearchFilterSummary from "../components/VehicleSearchFilterSummary";
import VehicleVariantCard from "../components/VehicleVariantCard";

export const DEFAULT_PAGE_SIZE = 16;

type CompareVehicleInputs = {
  vehiclesInComparison: APVehicleVariant[];
};

const DossierVehicleSearchResults = () => {
  const [filterSummaryExpanded, setFilterSummaryExpanded] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<VehicleVariantSearchFilters>(
    buildVehicleVariantSearchQueryFromParams(searchParams, DEFAULT_PAGE_SIZE)
  );
  const [paginationHistory, setPaginationHistory] = useState<string[]>([]);
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const [selectedVariant, setSelectedVariant] =
    useState<APVehicleVariant | null>(null);
  const navigate = useNavigate();
  const { getScopedPageUrl } = useAuth();
  const { shouldPrompt: shouldPromptForAIConsent } = useAIConsent();
  const { dossierId } = useParams();
  const vehicleCompareElementRef = useRef<HTMLElement>(null);
  const { handleSubmit, setValue, watch } = useForm<CompareVehicleInputs>({
    mode: "onTouched",
    defaultValues: { vehiclesInComparison: [] },
  });
  const expandCollapseAll = useExpandCollapseAll();
  const vehiclesInComparison = watch("vehiclesInComparison");
  const quoteId = searchParams.get("quoteId") || undefined;
  const searchEventId = searchParams.get("searchEventId") || undefined;

  if (!dossierId) {
    // TODO: improve client side error messaging
    throw new Error("dossier id param is misisng");
  }

  const dossier = getDossier(dossierId);
  const {
    faqProps,
    isLoading: isLoadingFaqs,
    isSuccess: isSuccessFaqs,
  } = useFaqs({
    pageId: "vehicle-search-results",
    contextIds: {
      opportunityId: dossier.data?.opportunity?.id,
    },
  });

  const vehicleAspects = getVehicleAspects();

  const items = searchVehicleVariants(filters, {
    keepPreviousData: true,
  });

  if (dossier.isError) {
    throw dossier.error;
  }
  if (vehicleAspects.isError) {
    throw vehicleAspects.error;
  }

  const onToggleFilterSummary = () => {
    setFilterSummaryExpanded((expanded) => !expanded);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [filters]);

  useEffect(() => {
    setPaginationHistory([]);
    setFilters(
      buildVehicleVariantSearchQueryFromParams(searchParams, DEFAULT_PAGE_SIZE)
    );
  }, [searchParams]);

  useEffect(() => {
    if (vehiclesInComparison.length === 0 && compareModalOpen) {
      setCompareModalOpen(false);
    }
  }, [vehiclesInComparison]);

  const primaryQuoteId = dossier.data?.opportunity?.primaryQuoteId;

  const {
    showOverwriteModal,
    handleQuoteAction,
    handleOverwriteConfirm,
    handleOverwriteCancel,
    oldestQuote,
  } = useQuoteLimit({
    dossierId: dossierId || "",
    primaryQuoteId: primaryQuoteId || null,
    onRedirect: (overwriteQuoteId?: string) => {
      if (selectedVariant) {
        const { cemakeId, cemodelId, year, cevariantId } = selectedVariant;

        const quoteParams = {
          makeId: cemakeId,
          modelId: cemodelId,
          modelYear: year,
          variantId: cevariantId,
          overwriteQuote: overwriteQuoteId,
          vehicleSearchEventId: searchEventId,
        };
        const queryString = buildQuoteQueryString(quoteParams);

        if (quoteId) {
          navigate(`${getScopedPageUrl("editQuote", quoteId)}${queryString}`);
        } else if (primaryQuoteId) {
          navigate(
            `${getScopedPageUrl("cloneQuote", primaryQuoteId)}${queryString}`
          );
        } else {
          navigate(
            `${getScopedPageUrl("createQuote", dossierId)}${queryString}`
          );
        }
      }
    },
    onCancel: () => {
      setSelectedVariant(null);
    },
  });

  useEffect(() => {
    if (selectedVariant) {
      handleQuoteAction(!!quoteId);
    }
  }, [selectedVariant]);

  const handlePageChange = (
    nextPageLink: string | undefined,
    paginationHistory: string[]
  ) => {
    setPaginationHistory(paginationHistory);
    setFilters({ ...filters, nextPageLink });
  };

  const onSortChange = (optionId: VehicleVariantSearchSortOption) => {
    searchParams.set("sort", optionId);
    setSearchParams(searchParams);
  };

  const onFilter = () => {
    navigate({
      pathname: getScopedPageUrl("dossierVehicleSearch", dossierId),
      search: `?${searchParams.toString()}`,
    });
  };

  const onToggleCompare = async (vehicleVariant: APVehicleVariant) => {
    const isIncluded = vehiclesInComparison
      .map((v) => v.id)
      .includes(vehicleVariant.id);

    if (isIncluded) {
      const newVehiclesInComparison = vehiclesInComparison.filter(
        (v) => v.id !== vehicleVariant.id
      );
      setValue("vehiclesInComparison", newVehiclesInComparison);
    } else if (vehiclesInComparison.length < 5) {
      const newVehiclesInComparison = [...vehiclesInComparison, vehicleVariant];
      setValue("vehiclesInComparison", newVehiclesInComparison);
    }
  };

  const onAIReview = async (vehicleVariant: APVehicleVariant) => {
    navigate({
      pathname: getScopedPageUrl(
        "dossierVehicleReview",
        dossierId,
        vehicleVariant.redbookVehicleCode
      ),
      search: quoteId ? `?quoteId=${quoteId}` : undefined,
    });
  };

  const onVehicleSpecs = async (vehicleVariant: APVehicleVariant) => {
    navigate({
      pathname: getScopedPageUrl(
        "dossierVehicleDetail",
        dossierId,
        vehicleVariant.redbookVehicleCode
      ),
      search: quoteId ? `?quoteId=${quoteId}` : undefined,
    });
  };

  const onQuote = async (vehicleVariant: APVehicleVariant) => {
    setSelectedVariant(vehicleVariant);
  };

  const onSubmit: SubmitHandler<CompareVehicleInputs> = async (_data) => {
    const variantCodes = _data.vehiclesInComparison.map(
      (v) => v.redbookVehicleCode
    );
    navigate({
      pathname: getScopedPageUrl("dossierVehicleComparison", dossierId),
      search: `?codes=${variantCodes.join(",")}${
        quoteId ? `&quoteId=${quoteId}` : ""
      }`,
    });
  };

  const isLoading =
    dossier.isLoading ||
    items.isLoading ||
    vehicleAspects.isLoading ||
    isLoadingFaqs;
  const isSuccess =
    dossier.isSuccess &&
    items.isSuccess &&
    vehicleAspects.isSuccess &&
    isSuccessFaqs;

  const renderVehicleVariantCard = (variant: APVehicleVariant) => {
    const isBeingCompared = vehiclesInComparison
      .map((v) => v.id)
      .includes(variant.id);

    return (
      <Box key={variant.id}>
        <VehicleVariantCard
          vehicleVariant={variant}
          config={{
            disableCardLink: false,
            isBeingCompared: isBeingCompared,
            disableCompare:
              !isBeingCompared && vehiclesInComparison.length >= 5,
            shouldPromptForAIConsent: shouldPromptForAIConsent,
            forceOpen: expandCollapseAll.isAllExpanded,
            forceClose: expandCollapseAll.isAllCollapsed,
          }}
          actions={{
            topActions: {
              onToggleCompare: onToggleCompare,
            },
            bottomActions: {
              onAIReview: onAIReview,
              onQuote: onQuote,
            },
            expandActions: {
              onVehicleSpecs: onVehicleSpecs,
            },
          }}
        />
      </Box>
    );
  };

  const renderItems = (items: APVehicleVariant[]) => {
    return <>{items.map(renderVehicleVariantCard)}</>;
  };

  const renderHeaderContent = (includeFaq?: boolean) => {
    const vehicleAspectOptions = vehicleAspects.data
      ? buildVehicleAspectOptions(vehicleAspects.data)
      : undefined;
    const inputs = vehicleAspectOptions
      ? buildVehicleVariantSearchInputsFromParams(
          searchParams,
          vehicleAspectOptions
        )
      : undefined;

    return (
      <Stack spacing={2} sx={{ width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "start",
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Button
              size="large"
              color="primary"
              variant="contained"
              disableElevation={true}
              onClick={handleSubmit(onSubmit)}
              disabled={vehiclesInComparison.length < 2}
            >
              Compare
            </Button>
            {vehiclesInComparison.length > 0 && (
              <APButtonLink
                text={`${vehiclesInComparison.length} vehicles selected`}
                onClick={() => setCompareModalOpen(true)}
              />
            )}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Box sx={{ whiteSpace: "nowrap" }}>
              <APButton
                variant="contained"
                text={"Refine Search"}
                color="primary"
                onClick={onFilter}
              />
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            columnGap: 4,
          }}
        >
          {inputs && (
            <Box>
              <VehicleSearchFilterSummary
                inputs={inputs}
                valueFormatters={VehicleVariantValueFormatters}
                fieldLabelMap={VehicleVariantFieldLabelMap}
                expanded={filterSummaryExpanded}
                onExpandToggle={onToggleFilterSummary}
              />
            </Box>
          )}
          {includeFaq && (
            <Box mr={1}>
              {renderAppLocation(
                "VehicleSearchList-Page",
                <InfoButton />,
                true,
                true
              )}
            </Box>
          )}
        </Box>
      </Stack>
    );
  };

  const renderAppLocation = faqProps.renderAppLocation;

  return (
    <PageContainer
      loading={isLoading}
      title="Vehicle Search"
      titleButton={
        <Box
          sx={{
            width: {
              xs: "168px",
              sm: "190px",
            },
            display: "flex",
            justifyContent: "end",
            columnGap: 1,
          }}
        >
          <APSelectDropDown
            label="Sort By"
            selectedOptionId={
              (searchParams.get("sort") as VehicleVariantSearchSortOption) ||
              "priceDesc"
            }
            options={VehicleVariantSearchSortOptionsList}
            onOptionChange={onSortChange}
          />
        </Box>
      }
    >
      {isSuccess && (
        <>
          <Box mb={2}>
            <RestrictedAppScope scope="internal">
              <CopyExternalUrlField text="Copy the URL below to share this search (this is a driver specific url):" />
            </RestrictedAppScope>
          </Box>

          <Box ref={vehicleCompareElementRef}>{renderHeaderContent(true)}</Box>

          <APStickyHeader anchorElement={vehicleCompareElementRef}>
            {renderHeaderContent()}
          </APStickyHeader>

          <APModal
            isOpen={compareModalOpen}
            onClose={() => setCompareModalOpen(false)}
            closeText="Close"
            confirmButton={
              <APButton
                variant="contained"
                text="Compare"
                onClick={handleSubmit(onSubmit)}
                disabled={vehiclesInComparison.length < 2}
              />
            }
          >
            <Stack spacing={1} sx={{ minHeight: "300px" }}>
              {renderItems(vehiclesInComparison)}
            </Stack>
          </APModal>

          <OverwriteQuoteModal
            isOpen={showOverwriteModal}
            onClose={handleOverwriteCancel}
            onConfirm={handleOverwriteConfirm}
            oldestQuote={oldestQuote}
          />

          <Stack spacing={2} mt={1}>
            <Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }} mb={1}>
                <ExpandCollapseAllButton controller={expandCollapseAll} />
              </Box>
              <PaginationContainer
                paginationHistory={paginationHistory}
                currentPage={paginationHistory.length + 1}
                pageSize={DEFAULT_PAGE_SIZE}
                itemCount={items.data?.count || 0}
                onPageChange={handlePageChange}
                nextPageLink={items.data?.nextPageLink || ""}
                isLoading={items.isFetching}
              >
                <Stack
                  spacing={0}
                  sx={{
                    display: "grid",
                    gap: "16px",
                    gridTemplateColumns: { md: "1fr 1fr" },
                  }}
                >
                  {renderItems(items.data?.items || [])}
                </Stack>
              </PaginationContainer>
            </Box>

            <Stack
              spacing={2}
              sx={{
                display: "flex",
                flexDirection: "column",
                pt: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  columnGap: 1,
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Box
                    sx={{ display: "flex", columnGap: 1, alignItems: "center" }}
                  >
                    <BackButton />
                    <HomeButton destination="dossiersHome" />
                  </Box>
                </Box>
              </Box>
              <Box>
                <RestrictedAppScope scope="external">
                  <APContactHelpButton
                    regardingOpportunityId={
                      dossier.data.opportunity?.id ?? undefined
                    }
                  />
                </RestrictedAppScope>
              </Box>
              <CopyMarketingUrl type="dossierVehicleSearchResults" />
            </Stack>
          </Stack>
        </>
      )}
    </PageContainer>
  );
};

export default DossierVehicleSearchResults;
